import { Suspense, lazy } from 'react';
import { Route, useParams } from 'react-router';
import { isValidReportType } from '@sr/types';
import { routePathsFollowup } from 'core/lib/routePaths/followupRoutePaths';
import { RouteLoader } from '#components/Loader/RouteLoader';
import { ErrorPage } from '#components/Error/index.js';

const Followup = lazy(() => import('followup/central-routes/react-routes'));

const FollowupOverview = lazy(() =>
  import('followup/central-routes/react-routes').then((module) => ({
    default: module.FollowupOverview,
  })),
);
const FollowupDatapage = lazy(() =>
  import('followup/central-routes/react-routes').then((module) => ({
    default: module.FollowupDatapage,
  })),
);
const FollowupAnalysispage = lazy(() =>
  import('followup/central-routes/react-routes').then((module) => ({
    default: module.FollowupAnalysispage,
  })),
);
const FollowupInvoices = lazy(() =>
  import('followup/central-routes/react-routes').then((module) => ({
    default: module.FollowupInvoices,
  })),
);

const FollowupInvoicesLayout = lazy(() =>
  import('followup/central-routes/react-routes').then((module) => ({
    default: module.FollowupInvoicesLayout,
  })),
);
const FollowupDocuments = lazy(() =>
  import('followup/central-routes/react-routes').then((module) => ({
    default: module.FollowupDocuments,
  })),
);
const FollowupPackagingCompensation = lazy(() =>
  import('followup/central-routes/react-routes').then((module) => ({
    default: module.FollowupPackagingCompensation,
  })),
);

const FollowupPackagingCompensationConfirmation = lazy(() =>
  import('followup/central-routes/react-routes').then((module) => ({
    default: module.FollowupPackagingCompensationConfirmation,
  })),
);

const FollowupPackagingCompensationSignUp = lazy(() =>
  import('followup/central-routes/react-routes').then((module) => ({
    default: module.FollowupPackagingCompensationSignUp,
  })),
);

// Wrapper component to validate context parameter at the layout level
const FollowupWithValidation = () => {
  const { context } = useParams<{ context: string }>();

  // If there's a context parameter, validate it
  if (context && !isValidReportType(context)) {
    return <ErrorPage errorType="PAGE_NOT_FOUND" />;
  }

  // Otherwise, render the Followup layout with nested routes
  return <Followup />;
};

export const FollowupRouteTree = (
  <Route
    path={routePathsFollowup.HOME}
    element={
      <Suspense fallback={<RouteLoader />}>
        <FollowupWithValidation />
      </Suspense>
    }
  >
    <Route index path={routePathsFollowup.HOME} element={<FollowupOverview />} />
    <Route path={routePathsFollowup.DATA} element={<FollowupDatapage />} />
    <Route path={routePathsFollowup.DATAPAGE_W_SETTINGS} element={<FollowupDatapage />} />
    <Route
      path={routePathsFollowup.DATAPAGE_ARTICLES_SETTINGS_ARTICLEBREAKDOWN}
      element={<FollowupDatapage />}
    />
    <Route path={routePathsFollowup.ANALYSIS} element={<FollowupAnalysispage />} />
    <Route path={routePathsFollowup.ANALYSIS_W_SETTINGS} element={<FollowupAnalysispage />} />
    <Route
      path={routePathsFollowup.ANALYSIS_W_SETTINGS_SUBGRAPH}
      element={<FollowupAnalysispage />}
    />
    <Route path={routePathsFollowup.INVOICES} element={<FollowupInvoices />} />
    <Route path={routePathsFollowup.INVOICES_LAYOUT} element={<FollowupInvoicesLayout />} />
    <Route path={routePathsFollowup.DOCUMENTS} element={<FollowupDocuments />} />
    <Route path={routePathsFollowup.NOTE_OBLIGATION} element={<FollowupDocuments />} />
    <Route path={routePathsFollowup.CERTIFICATE_OF_DESTRUCTION} element={<FollowupDocuments />} />
    <Route path={routePathsFollowup.ANIMAL_BY_PRODUCTS} element={<FollowupDocuments />} />
    <Route path={routePathsFollowup.OIL_SEPARATOR} element={<FollowupDocuments />} />
    <Route path={routePathsFollowup.PACKAGING_COMPENSATION}>
      <Route index element={<FollowupPackagingCompensation />} />
      <Route
        path={routePathsFollowup.PACKAGING_COMPENSATION_SIGN_UP}
        element={<FollowupPackagingCompensationSignUp />}
      />
      <Route
        path={routePathsFollowup.PACKAGING_COMPENSATION_CONFIRMATION}
        element={<FollowupPackagingCompensationConfirmation />}
      />
    </Route>
  </Route>
);

export const preloadFollowup = () => {
  //Import default export to trigger preload
  void import('followup/central-routes/react-routes');
};
